/**
 * Locate a finding's evidence snippet on a PDF page (Part VIII).
 *
 * Uses pdf.js text content (string runs + positions) to find where a
 * check/risk/error finding's `evidence_snippet` sits on its `page_ref` page, and
 * returns a normalised (0-1) bounding box for the highlight overlay. Honest by
 * design: when the snippet can't be confidently matched (paraphrased snippet,
 * scanned page, hyphenation), it returns null and the caller falls back to a
 * clear full-page-edge highlight instead of guessing a false-precise box.
 *
 * Results are memoised per (document, page, snippet) - text extraction runs once
 * per page no matter how many findings point at it.
 */
import type { PDFDocumentProxy } from 'pdfjs-dist';

export interface NormalisedRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PlacedItem {
  /** Normalised char range of this item inside the page's normalised string. */
  start: number;
  end: number;
  rect: NormalisedRect;
}

interface PageTextIndex {
  text: string;
  items: PlacedItem[];
}

/** Lowercase, collapse all whitespace runs; keep letters/digits and basic punctuation so offsets stay meaningful. */
function normalise(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ');
}

const pageIndexCache = new Map<string, Promise<PageTextIndex>>();
const locateCache = new Map<string, NormalisedRect | null>();

function docKey(doc: PDFDocumentProxy): string {
  return doc.fingerprints?.[0] ?? 'doc';
}

async function buildPageIndex(doc: PDFDocumentProxy, pageNumber: number): Promise<PageTextIndex> {
  const page = await doc.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1 });
  const content = await page.getTextContent();

  let text = '';
  const items: PlacedItem[] = [];
  for (const raw of content.items) {
    if (!('str' in raw) || raw.str.trim() === '') continue;
    const piece = normalise(raw.str);
    if (piece === '') continue;
    const start = text === '' ? 0 : text.length + 1;
    text = text === '' ? piece : `${text} ${piece}`;

    const tx = raw.transform as number[];
    const x = tx[4] ?? 0;
    const yBaseline = tx[5] ?? 0;
    const h = raw.height || Math.abs(tx[3] ?? 10);
    const w = raw.width || 0;
    items.push({
      start,
      end: start + piece.length,
      rect: {
        x: x / viewport.width,
        // PDF y-axis is bottom-up; the overlay's is top-down.
        y: 1 - (yBaseline + h) / viewport.height,
        w: w / viewport.width,
        h: h / viewport.height,
      },
    });
  }
  return { text, items };
}

function unionRects(rects: NormalisedRect[]): NormalisedRect {
  let x0 = 1;
  let y0 = 1;
  let x1 = 0;
  let y1 = 0;
  for (const r of rects) {
    x0 = Math.min(x0, r.x);
    y0 = Math.min(y0, r.y);
    x1 = Math.max(x1, r.x + r.w);
    y1 = Math.max(y1, r.y + r.h);
  }
  const PAD = 0.004;
  return {
    x: Math.max(0, x0 - PAD),
    y: Math.max(0, y0 - PAD),
    w: Math.min(1, x1 + PAD) - Math.max(0, x0 - PAD),
    h: Math.min(1, y1 + PAD) - Math.max(0, y0 - PAD),
  };
}

/**
 * Find the snippet's bounding box on the page, or null when it can't be matched.
 * Tries the full normalised snippet first, then a shorter prefix (snippets often
 * drift from the exact page text toward the end).
 */
export async function locateSnippet(
  doc: PDFDocumentProxy,
  pageNumber: number,
  snippet: string,
): Promise<NormalisedRect | null> {
  const needleFull = normalise(snippet).trim();
  if (needleFull.length < 8) return null;

  const cacheKey = `${docKey(doc)}:${pageNumber}:${needleFull}`;
  if (locateCache.has(cacheKey)) return locateCache.get(cacheKey) ?? null;

  const indexKey = `${docKey(doc)}:${pageNumber}`;
  let indexPromise = pageIndexCache.get(indexKey);
  if (!indexPromise) {
    indexPromise = buildPageIndex(doc, pageNumber);
    pageIndexCache.set(indexKey, indexPromise);
  }

  let result: NormalisedRect | null = null;
  try {
    const index = await indexPromise;
    const candidates = [needleFull, needleFull.slice(0, 60).trim(), needleFull.slice(0, 32).trim()];
    for (const needle of candidates) {
      if (needle.length < 8) continue;
      const at = index.text.indexOf(needle);
      if (at === -1) continue;
      const matchEnd = at + needle.length;
      const rects = index.items.filter((item) => item.end > at && item.start < matchEnd).map((item) => item.rect);
      if (rects.length === 0) continue;
      // A match spread over most of the page height is a false positive of the
      // shortened needle, not a real location - reject rather than mislead.
      const union = unionRects(rects);
      if (union.h > 0.5) continue;
      result = union;
      break;
    }
  } catch {
    result = null;
  }
  locateCache.set(cacheKey, result);
  return result;
}
